import React from 'react';
import {
  Dialog, DialogContent, Stack, Typography, CircularProgress, useTheme, alpha,
} from '@mui/material';
import { PictureAsPdfOutlined } from '@mui/icons-material';

interface Props {
  open: boolean;
  /** Short label — "Preparing PDF", "Rendering 128 admit cards". Keep terse. */
  label: string;
  /** Optional second-line explainer — "This may take a few seconds." */
  hint?: string;
}

/**
 * Modal spinner shown while a PDF is being rendered on the client. Use for
 * bulk documents (admit-card packs, long merit lists) where the main thread
 * can block for several seconds — the modal keeps the user from clicking
 * the trigger again and signals that work is happening.
 *
 * For instant single-record exports, just toggle the trigger button's label
 * via `usePdfExport().loading` — no modal needed.
 */
const PdfRenderingDialog: React.FC<Props> = ({ open, label, hint }) => {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => { /* no-op — modal cannot be dismissed while rendering */ }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            width: 360,
            textAlign: 'center',
            py: 1,
          },
        },
      }}
    >
      <DialogContent sx={{ py: 4 }}>
        <Stack spacing={2.5} alignItems="center">
          <Stack
            sx={{
              width: 68, height: 68, borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              position: 'relative',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            <CircularProgress
              size={62}
              thickness={2.5}
              sx={{ position: 'absolute', inset: 3, color: theme.palette.primary.main }}
            />
            <PictureAsPdfOutlined sx={{ color: theme.palette.primary.main, fontSize: 26 }} />
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={700}>{label}</Typography>
            {hint && (
              <Typography variant="caption" color="text.secondary">{hint}</Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PdfRenderingDialog;
